import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AskCtx = createContext(null);
export const useAsk = () => useContext(AskCtx);

const POLL_MS = 2500;

export function AskProvider({ children }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const pollRef = useRef(null);

  const refreshThreads = useCallback(async () => {
    try {
      const list = await endpoints.ask.listThreads();
      setThreads(list);
      setLoaded(true);
    } catch (e) { /* auth or transient — ignore */ }
  }, []);

  useEffect(() => {
    if (!user || user === false) {
      setThreads([]);
      clearInterval(pollRef.current);
      return;
    }
    refreshThreads();
    pollRef.current = setInterval(refreshThreads, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [user, refreshThreads]);

  // queueMessage: creates a thread and returns { id }. Caller decides whether to navigate.
  const queueMessage = useCallback(async (text, { title } = {}) => {
    const t = await endpoints.ask.createThread(text, title);
    // Optimistic prepend so nav lights up immediately
    setThreads((prev) => [{ ...t, status: "running" }, ...prev.filter((x) => x.id !== t.id)]);
    refreshThreads();
    return t;
  }, [refreshThreads]);

  const addMessage = useCallback(async (threadId, content) => {
    await endpoints.ask.addMessage(threadId, content);
    // Bump status locally to "running" so the sidebar spinner appears immediately
    setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, status: "running" } : t)));
    refreshThreads();
  }, [refreshThreads]);

  const deleteThread = useCallback(async (threadId) => {
    await endpoints.ask.deleteThread(threadId);
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    if (activeThreadId === threadId) setActiveThreadId(null);
  }, [activeThreadId]);

  const renameThread = useCallback(async (threadId, title) => {
    await endpoints.ask.patchThread(threadId, { title });
    setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, title } : t)));
  }, []);

  const value = {
    threads, loaded, activeThreadId, setActiveThreadId,
    refreshThreads, queueMessage, addMessage, deleteThread, renameThread,
  };
  return <AskCtx.Provider value={value}>{children}</AskCtx.Provider>;
}
